import am5locales_ru_RU from "@amcharts/amcharts5/locales/ru_RU";
import am5locales_zh_Hans from "@amcharts/amcharts5/locales/zh_Hans";

const LOCALE_MAP = {
  ru: "ru-RU",
  en: "en-US",
  kz: "kk-KZ",
  cn: "zh-Hans",
};

const AM_CHARTS_LOCALE_MAP = {
  ru: am5locales_ru_RU,
  en: null,
  kz: am5locales_ru_RU,
  cn: am5locales_zh_Hans,
};

const DATE_SEPARATOR = ".";

const getLocale = (language) => LOCALE_MAP[language] || "ru-RU";

const parseDate = (dateString) => {
  const [day, month, year] = dateString.split(DATE_SEPARATOR);
  return new Date(year, month - 1, day);
};

const createMonthKey = (year, month) => {
  return `${year}-${String(month).padStart(2, "0")}`;
};

const formatDate = (date, language) => {
  return date.toLocaleDateString(getLocale(language), {
    day: "2-digit",
    month: "short",
  });
};

const formatMonth = (date, language, showYear = false) => {
  const formatOptions = { month: "short" };
  if (showYear) {
    formatOptions.year = "numeric";
  }
  return date.toLocaleDateString(getLocale(language), formatOptions);
};

export const formatDateWithDay = (date, language = "ru", showYear = false) => {
  const locale = getLocale(language);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString(locale, { month: "long" });
  const year = date.getFullYear().toString();

  return showYear ? `${day}\n${month}\n${year}` : `${day}\n${month}`;
};

const enrichDataWithDates = (data) => {
  return data.map((item) => {
    const isoDate = parseDate(item.date);
    const [, month, year] = item.date.split(DATE_SEPARATOR);
    return {
      ...item,
      isoDate,
      monthKey: createMonthKey(year, month),
    };
  });
};

const sortByDate = (data) => {
  return [...data].sort((a, b) => a.isoDate - b.isoDate);
};

const hasMultipleYears = (data) => {
  const years = new Set(data.map((item) => item.isoDate.getFullYear()));
  return years.size > 1;
};

const groupDataByMonth = (data, numericFields) => {
  const groupedData = {};

  data.forEach((item) => {
    const { monthKey, isoDate } = item;

    if (!groupedData[monthKey]) {
      groupedData[monthKey] = {
        monthKey,
        isoDate: new Date(isoDate.getFullYear(), isoDate.getMonth(), 1),
        count: 0,
      };

      numericFields.forEach((field) => {
        groupedData[monthKey][field] = 0;
      });
    }

    numericFields.forEach((field) => {
      if (typeof item[field] === "number") {
        groupedData[monthKey][field] += item[field];
      }
    });

    if (!numericFields.includes("count")) {
      groupedData[monthKey].count++;
    }
  });

  return Object.values(groupedData);
};

export const processDataByMonth = (data, language = "ru", numericFields = []) => {
  if (!data || data.length === 0) {
    return [];
  }

  const enrichedData = enrichDataWithDates(data);
  const sortedData = sortByDate(enrichedData);
  const showYear = hasMultipleYears(sortedData);
  const groupedData = groupDataByMonth(sortedData, numericFields);

  return groupedData.map((item) => ({
    ...item,
    month: formatMonth(item.isoDate, language, showYear),
    date: formatMonth(item.isoDate, language, showYear),
  }));
};

export const getAmChartsLocale = (language) => {
  return AM_CHARTS_LOCALE_MAP[language];
};

export const translateMonthLabels = (data, language = "ru") => {
  if (!data || !Array.isArray(data)) return [];

  const locale = getLocale(language);
  const years = new Set(data.map((item) => item.month.split(DATE_SEPARATOR)[1]));
  const showYear = years.size > 1;

  return data.map((item) => {
    const [monthStr, yearStr] = item.month.split(DATE_SEPARATOR);
    const date = new Date(`${yearStr}-${monthStr.padStart(2, "0")}-01`);

    return {
      ...item,
      month: date.toLocaleDateString(locale, {
        month: "short",
        ...(showYear && { year: "numeric" }),
      }),
    };
  });
};

export const shouldShowYear = (data) => {
  if (!data || data.length === 0) return false;

  const years = new Set(
    data.map((item) => {
      if (item.isoDate) {
        return item.isoDate.getFullYear();
      }
      const [, , year] = item.date.split(DATE_SEPARATOR);
      return parseInt(year);
    }),
  );

  return years.size > 1;
};

export const processPriceDataByDay = (data, language = "ru") => {
  if (!data || data.length === 0) {
    return [];
  }

  const showYear = shouldShowYear(data);

  return data
    .map((item) => {
      const isoDate = parseDate(item.date);
      const [day, month, year] = item.date.split(DATE_SEPARATOR);

      return {
        ...item,
        isoDate,
        month: formatDateWithDay(isoDate, language, showYear),
        dayKey: createMonthKey(year, month) + `-${day.padStart(2, "0")}`,
      };
    })
    .sort((a, b) => a.isoDate - b.isoDate);
};

export const processTransportData = (data, language) => {
  const numericFields = ["awning", "refrigerator", "isothermal", "covered"];
  return processDataByMonth(data, language, numericFields);
};

export const processPriceData = (data, language) => {
  const numericFields = ["price", "algoPrice"];
  return processDataByMonth(data, language, numericFields);
};

export const processOrderCountData = (data, language) => {
  const numericFields = ["count"];
  return processDataByMonth(data, language, numericFields);
};
